import { useTranslation } from 'react-i18next';
import { Link2 } from 'lucide-react';
import { type ProcessStepRecordingDisplay, type PopulatedUser } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { DELETED_ENTITY_CLASS } from '@/lib/useDeletedEntity';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  appendUserStatusSuffix,
  cn,
  getUserInitials,
  getUserAvatarUrl,
  isAssigneeDeleted,
  isUserDeletedInLookup,
  lookupAssignee
} from '@bliks-io/ui';

interface ProcessStepMembersCellProps {
  step: ProcessStepRecordingDisplay;
}

export function ProcessStepMembersCell({ step }: ProcessStepMembersCellProps) {
  const { t } = useTranslation();

  const recordedBy = step.recordedBy;
  const assignedToModel = step.assignedToModel;
  const lookup = useAssigneeLookup();
  // `step.assignedTo` is an unpopulated `{ type, id }` ref — resolve it to the
  // populated entity against the org lists.
  const assignedTo = lookupAssignee(step.assignedTo, assignedToModel, lookup);
  const whoElseDoesThis = step.whoElseDoesThis || [];

  // The lookup-resolved entities carry `deletedAt`; recordedBy is a
  // backend-populated user (deletedAt stripped), so its tombstone only
  // survives in the lookup. `recordedBy` is null when its user was
  // hard-deleted (dangling ref) — fall back to the unknown-user label.
  const isAssignedToDeleted = isAssigneeDeleted(assignedTo);
  const isRecordedByDeleted = recordedBy ? isUserDeletedInLookup(recordedBy._id, lookup) : false;
  const recordedByName = recordedBy
    ? appendUserStatusSuffix(
        `${recordedBy.firstName} ${recordedBy.lastName}`,
        lookup.users?.get(recordedBy._id) ?? recordedBy,
        t
      )
    : t('viralRecording.labels.unknownUser');

  // Resolve accepted and pending users' `{ type, id }` refs against the org
  // lists; unresolvable refs are dropped. Only Users get avatar display
  // (Teams don't have avatars in the same way).
  const populatedWhoElse = whoElseDoesThis.flatMap((item) => {
    if (item.status !== 'accepted' && item.status !== 'pending') return [];
    if (item.model !== 'User') return [];
    const user = lookupAssignee(item.entity, 'User', lookup);
    return user ? [{ entity: user as PopulatedUser, status: item.status }] : [];
  });

  // Get entity icon for non-User assignedTo types
  const getEntityIcon = () => {
    if (!assignedToModel || assignedToModel === 'User') return null;
    switch (assignedToModel) {
      case 'OrgFunction':
        return ENTITY_ICONS.team;
      case 'System':
        return ENTITY_ICONS.system;
      case 'ExternalContact':
        return ENTITY_ICONS.externalContact;
      default:
        return null;
    }
  };

  // Get the display name for assignedTo based on entity type
  const getAssignedToInfo = (): { name: string; type: string } | null => {
    if (!assignedTo || !assignedToModel) return null;

    switch (assignedToModel) {
      case 'User': {
        const user = assignedTo as { firstName?: string; lastName?: string };
        if (user.firstName && user.lastName) {
          return { name: `${user.firstName} ${user.lastName}`, type: t('processPage.Person') };
        }
        return null;
      }
      case 'OrgFunction': {
        const orgFunction = assignedTo as { name?: string };
        if (orgFunction.name) {
          return { name: orgFunction.name, type: t('common.entityTypes.OrgFunction') };
        }
        return null;
      }
      case 'System': {
        const system = assignedTo as { name?: string };
        if (system.name) {
          return { name: system.name, type: t('common.entityTypes.SoftwareSystem') };
        }
        return null;
      }
      case 'ExternalContact': {
        const contact = assignedTo as { name?: string; firstName?: string; lastName?: string };
        const name =
          contact.name ||
          (contact.firstName && contact.lastName
            ? `${contact.firstName} ${contact.lastName}`
            : null);
        if (name) {
          return { name, type: t('common.entityTypes.ExternalContact') };
        }
        return null;
      }
      default:
        return null;
    }
  };

  const EntityIcon = getEntityIcon();
  const assignedToInfo = getAssignedToInfo();
  const isNonUserAssignee = assignedToModel && assignedToModel !== 'User' && EntityIcon;

  // Show max 2 whoElseDoesThis avatars, rest as +N
  const visibleWhoElse = populatedWhoElse.slice(0, 2);
  const remainingCount = populatedWhoElse.length - visibleWhoElse.length;

  // Render the assignedTo section (either user avatar or entity icon + link + recordedBy)
  const renderAssignedToSection = () => {
    if (isNonUserAssignee) {
      return (
        <div className="flex items-center gap-1">
          <div
            className={cn(
              'h-8 w-8 rounded-lg bg-gray-2 border border-border-12 flex items-center justify-center',
              isAssignedToDeleted && DELETED_ENTITY_CLASS
            )}
          >
            <EntityIcon className="h-4 w-4 text-gray-8" />
          </div>
          <Link2 className="h-3.5 w-3.5 text-gray-8" />
          <Avatar
            className={cn(
              'h-8 w-8 border-2 border-white',
              isRecordedByDeleted && DELETED_ENTITY_CLASS
            )}
          >
            <AvatarImage
              src={getUserAvatarUrl(recordedBy ?? undefined, 'thumbnail')}
              alt={recordedBy?.firstName ?? ''}
            />
            <AvatarFallback className="text-xs">
              {getUserInitials(recordedBy?.firstName, recordedBy?.lastName)}
            </AvatarFallback>
          </Avatar>
        </div>
      );
    }

    const showsAssignedUser = assignedToModel === 'User' && assignedTo;
    const userToShow = showsAssignedUser
      ? (assignedTo as { firstName?: string; lastName?: string; avatar?: PopulatedUser['avatar'] })
      : recordedBy;
    const isUserToShowDeleted = showsAssignedUser ? isAssignedToDeleted : isRecordedByDeleted;

    return (
      <Avatar
        className={cn('h-8 w-8 border-2 border-white', isUserToShowDeleted && DELETED_ENTITY_CLASS)}
      >
        <AvatarImage
          src={userToShow ? getUserAvatarUrl(userToShow as PopulatedUser, 'thumbnail') : undefined}
          alt={userToShow?.firstName || ''}
        />
        <AvatarFallback className="text-xs">
          {getUserInitials(userToShow?.firstName || '', userToShow?.lastName || '')}
        </AvatarFallback>
      </Avatar>
    );
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 cursor-pointer">
            {renderAssignedToSection()}

            {populatedWhoElse.length > 0 && (
              <>
                <span className="text-gray-8 text-xs">○</span>
                <div className="flex items-center -space-x-3">
                  {visibleWhoElse.map((item, idx) => (
                    <Avatar
                      key={idx}
                      className={cn(
                        'h-8 w-8 border-2 border-white',
                        isUserDeletedInLookup(item.entity._id, lookup) && DELETED_ENTITY_CLASS
                      )}
                    >
                      <AvatarImage
                        src={getUserAvatarUrl(item.entity, 'thumbnail')}
                        alt={item.entity.firstName}
                      />
                      <AvatarFallback className="text-xs">
                        {getUserInitials(item.entity.firstName, item.entity.lastName)}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {remainingCount > 0 && (
                    <Avatar className="h-8 w-8 border-2 border-white">
                      <AvatarFallback className="text-xs font-medium bg-gray-2 text-gray-8">
                        +{remainingCount}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              </>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-2">
            {/* Always show assignedTo */}
            <div>
              <div className="text-xs text-muted-foreground">
                {t('processes.assignedTo')}
                {assignedToInfo ? ` (${assignedToInfo.type})` : ''}
              </div>
              <div className="font-medium">
                {assignedToInfo
                  ? appendUserStatusSuffix(assignedToInfo.name, assignedTo, t)
                  : recordedByName}
              </div>
            </div>
            {/* Show recordedBy only if different (non-User assignee) */}
            {isNonUserAssignee && (
              <div>
                <div className="text-xs text-muted-foreground">{t('processFlow.recordedBy')}</div>
                <div className="font-medium">{recordedByName}</div>
              </div>
            )}
            {/* Show whoElseDoesThis if any */}
            {populatedWhoElse.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground">
                  {t('processPage.whoElseDoesThis')}
                </div>
                <div className="flex flex-col gap-0.5">
                  {populatedWhoElse.map((item, idx) => (
                    <div key={idx} className="font-medium">
                      {appendUserStatusSuffix(
                        `${item.entity.firstName} ${item.entity.lastName}`,
                        lookup.users?.get(item.entity._id) ?? item.entity,
                        t
                      )}
                      {item.status === 'pending' && (
                        <span className="text-muted-foreground text-xs ml-1">
                          ({t('processPage.statusPending')})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
